import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from './contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import env from './utils/env';
import Loader from './components/Loader';
import {
    ISearchTarget,
    ISearchAttempt,
    formatDateTime,
    getEngineColor
} from './types/adminDebug';
import './ReportSourcesView.css';

const AdminSearchTargetDetailView: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const { token, authLoaded } = useAuth();
    const { t } = useTranslation();
    const navigate = useNavigate();

    const [target, setTarget] = useState<ISearchTarget | null>(null);
    const [attempts, setAttempts] = useState<ISearchAttempt[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const apiBaseUrl = env.apiUrl;
    const apiKey = env.apiKey;

    // Fetch search target details
    const fetchTarget = useCallback(async () => {
        if (!id) return;

        setLoading(true);
        setError(null);

        try {
            const headers: Record<string, string> = {
                'x-api-key': apiKey,
                'Content-Type': 'application/json'
            };

            if (token) {
                headers['Authorization'] = `Bearer ${token}`;
            }

            // Fetch target details
            const targetResponse = await fetch(`${apiBaseUrl}/search-targets/${id}`, {
                headers
            });

            if (!targetResponse.ok) {
                throw new Error('Failed to fetch search target');
            }

            const targetData = await targetResponse.json();
            setTarget(targetData);

            // Fetch related attempts
            const attemptsResponse = await fetch(`${apiBaseUrl}/search-targets/${id}/attempts?limit=50`, {
                headers
            });

            if (attemptsResponse.ok) {
                const attemptsData = await attemptsResponse.json();
                setAttempts(attemptsData.searchAttempts || []);
            }
        } catch (err: unknown) {
            const errorMessage = err instanceof Error ? err.message : 'Unknown error';
            setError(errorMessage);
        } finally {
            setLoading(false);
        }
    }, [id, token, apiBaseUrl, apiKey]);

    useEffect(() => {
        if (authLoaded && id) {
            fetchTarget();
        }
    }, [authLoaded, id, fetchTarget]);

    const handleBack = () => {
        navigate(-1);
    };

    if (!authLoaded || loading) {
        return <Loader text={t('common.loading')} />;
    }

    if (error) {
        return (
            <div className="container">
                <div className="error-message" style={{ color: 'red' }}>
                    {error}
                </div>
                <button onClick={handleBack} style={{ marginTop: '1rem' }}>
                    {t('adminDebug.backToList')}
                </button>
            </div>
        );
    }

    if (!target) {
        return (
            <div className="container">
                <div className="no-results">{t('adminDebug.searchTargets.notFound')}</div>
                <button onClick={handleBack} style={{ marginTop: '1rem' }}>
                    {t('adminDebug.backToList')}
                </button>
            </div>
        );
    }

    const labelStyle: React.CSSProperties = {
        textAlign: 'right',
        paddingRight: '15px',
        fontWeight: 'bold',
        color: '#666'
    };

    const valueStyle: React.CSSProperties = {
        textAlign: 'left',
        wordBreak: 'break-word'
    };

    const gridStyle: React.CSSProperties = {
        display: 'grid',
        gridTemplateColumns: '180px auto',
        columnGap: '15px',
        rowGap: '12px',
        alignItems: 'baseline'
    };

    const sectionStyle: React.CSSProperties = {
        marginBottom: '2rem',
        padding: '1.5rem',
        backgroundColor: 'var(--card-bg, #f9f9f9)',
        borderRadius: '8px',
        border: '1px solid var(--border-color, #e0e0e0)'
    };

    return (
        <div className="container">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                <h1>{t('adminDebug.searchTargets.detailTitle')}</h1>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <Link
                        to={`/admin/search-targets/${id}/pipeline`}
                        style={{
                            padding: '0.5rem 1rem',
                            backgroundColor: '#28a745',
                            color: 'white',
                            border: 'none',
                            borderRadius: '3px',
                            cursor: 'pointer',
                            textDecoration: 'none',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '0.25rem'
                        }}
                    >
                        <span className="material-icons" style={{ fontSize: '1rem' }}>account_tree</span>
                        {t('adminDebug.viewPipeline')}
                    </Link>
                    <button
                        onClick={handleBack}
                        style={{
                            padding: '0.5rem 1rem',
                            backgroundColor: '#007bff',
                            color: 'white',
                            border: 'none',
                            borderRadius: '3px',
                            cursor: 'pointer'
                        }}
                    >
                        {t('adminDebug.backToList')}
                    </button>
                </div>
            </div>

            {/* Basic Information */}
            <div style={sectionStyle}>
                <h2 style={{ marginBottom: '1rem' }}>{t('adminDebug.sections.basicInfo')}</h2>
                <div style={gridStyle}>
                    <div style={labelStyle}>{t('adminDebug.fields.id')}:</div>
                    <div style={valueStyle}>{target._id}</div>

                    {target.query && (
                        <>
                            <div style={labelStyle}>{t('adminDebug.fields.query')}:</div>
                            <div style={valueStyle}>{target.query}</div>
                        </>
                    )}

                    {target.issuer && (
                        <>
                            <div style={labelStyle}>{t('adminDebug.fields.issuer')}:</div>
                            <div style={valueStyle}>{target.issuer}</div>
                        </>
                    )}

                    {target.name && (
                        <>
                            <div style={labelStyle}>{t('adminDebug.fields.name')}:</div>
                            <div style={valueStyle}>{target.name}</div>
                        </>
                    )}

                    {target.category && (
                        <>
                            <div style={labelStyle}>{t('adminDebug.fields.category')}:</div>
                            <div style={valueStyle}>{target.category}</div>
                        </>
                    )}

                    {target.country && (
                        <>
                            <div style={labelStyle}>{t('adminDebug.fields.country')}:</div>
                            <div style={valueStyle}>{target.country}</div>
                        </>
                    )}

                    {target.market && (
                        <>
                            <div style={labelStyle}>{t('adminDebug.fields.market')}:</div>
                            <div style={valueStyle}>{target.market}</div>
                        </>
                    )}

                    {target.type && (
                        <>
                            <div style={labelStyle}>{t('adminDebug.fields.type')}:</div>
                            <div style={valueStyle}>{target.type}</div>
                        </>
                    )}

                    {target.source && (
                        <>
                            <div style={labelStyle}>{t('adminDebug.fields.source')}:</div>
                            <div style={valueStyle}>{target.source}</div>
                        </>
                    )}
                </div>
            </div>

            {/* Value Information */}
            {(target.value || target.currency) && (
                <div style={sectionStyle}>
                    <h2 style={{ marginBottom: '1rem' }}>{t('adminDebug.sections.valueInfo')}</h2>
                    <div style={gridStyle}>
                        {target.value !== undefined && (
                            <>
                                <div style={labelStyle}>{t('adminDebug.fields.value')}:</div>
                                <div style={valueStyle}>{target.value.toLocaleString()}</div>
                            </>
                        )}

                        {target.currency && (
                            <>
                                <div style={labelStyle}>{t('adminDebug.fields.currency')}:</div>
                                <div style={valueStyle}>{target.currency}</div>
                            </>
                        )}
                    </div>
                </div>
            )}

            {/* URLs */}
            {(target.bond_url || target.program_url || target.framework_url) && (
                <div style={sectionStyle}>
                    <h2 style={{ marginBottom: '1rem' }}>{t('adminDebug.sections.urls')}</h2>
                    <div style={gridStyle}>
                        {target.bond_url && (
                            <>
                                <div style={labelStyle}>{t('adminDebug.fields.bondUrl')}:</div>
                                <div style={valueStyle}>
                                    <a href={target.bond_url} target="_blank" rel="noopener noreferrer">
                                        {target.bond_url}
                                    </a>
                                </div>
                            </>
                        )}

                        {target.program_url && (
                            <>
                                <div style={labelStyle}>{t('adminDebug.fields.programUrl')}:</div>
                                <div style={valueStyle}>
                                    <a href={target.program_url} target="_blank" rel="noopener noreferrer">
                                        {target.program_url}
                                    </a>
                                </div>
                            </>
                        )}

                        {target.framework_url && (
                            <>
                                <div style={labelStyle}>{t('adminDebug.fields.frameworkUrl')}:</div>
                                <div style={valueStyle}>
                                    <a href={target.framework_url} target="_blank" rel="noopener noreferrer">
                                        {target.framework_url}
                                    </a>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}

            {/* Timestamps */}
            <div style={sectionStyle}>
                <h2 style={{ marginBottom: '1rem' }}>{t('adminDebug.sections.timestamps')}</h2>
                <div style={gridStyle}>
                    {target.search_timestamp && (
                        <>
                            <div style={labelStyle}>{t('adminDebug.fields.searchTimestamp')}:</div>
                            <div style={valueStyle}>{formatDateTime(target.search_timestamp)}</div>
                        </>
                    )}

                    {target.insert_date && (
                        <>
                            <div style={labelStyle}>{t('adminDebug.fields.insertDate')}:</div>
                            <div style={valueStyle}>{formatDateTime(target.insert_date)}</div>
                        </>
                    )}

                    {target.processed && (
                        <>
                            <div style={labelStyle}>{t('adminDebug.fields.processed')}:</div>
                            <div style={valueStyle}>{target.processed}</div>
                        </>
                    )}

                    {target.number_of_results !== undefined && (
                        <>
                            <div style={labelStyle}>{t('adminDebug.fields.numberOfResults')}:</div>
                            <div style={valueStyle}>{target.number_of_results}</div>
                        </>
                    )}
                </div>
            </div>

            {/* Related Search Attempts */}
            <div style={sectionStyle}>
                <h2 style={{ marginBottom: '1rem' }}>{t('adminDebug.sections.relatedAttempts')}</h2>
                {attempts.length > 0 ? (
                    <div className="table-container">
                        <table>
                            <thead>
                                <tr>
                                    <th>{t('adminDebug.fields.id')}</th>
                                    <th>{t('adminDebug.fields.timestamp')}</th>
                                    <th>{t('adminDebug.fields.engine')}</th>
                                    <th>{t('adminDebug.fields.numberOfResults')}</th>
                                    <th>{t('adminDebug.actions')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {attempts.map(attempt => (
                                    <tr key={attempt._id}>
                                        <td>{attempt._id.substring(0, 12)}...</td>
                                        <td>{formatDateTime(attempt.timestamp)}</td>
                                        <td>
                                            <span
                                                style={{
                                                    backgroundColor: getEngineColor(attempt.engine),
                                                    color: 'white',
                                                    padding: '2px 8px',
                                                    borderRadius: '4px',
                                                    fontSize: '0.85em'
                                                }}
                                            >
                                                {attempt.engine}
                                            </span>
                                        </td>
                                        <td>{attempt.number_of_results}</td>
                                        <td>
                                            <Link
                                                to={`/admin/search-attempts/${attempt._id}`}
                                                style={{ color: '#007bff' }}
                                            >
                                                {t('adminDebug.viewDetails')}
                                            </Link>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <div className="no-results">{t('adminDebug.searchAttempts.noResults')}</div>
                )}
            </div>
        </div>
    );
};

export default AdminSearchTargetDetailView;
